import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shadcn-in-nx/ui/tabs";
import { Button } from "@shadcn-in-nx/ui/button";
import { MessageSquare, Bug, Play, Loader2 } from "lucide-react";
import PlaygroundInput from "./PlaygroundInput";
import ConversationTab from "./ConversationTab";
import DebugTab from "./DebugTab";
import AgentResultTab from "./AgentResultTab";
import { AutomationTabTriggers, AutomationTabContents } from "./AutomationTabs";
import useWorkflowPolling from "./useWorkflowPolling";

export default function AutomationPlayground() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("conversation");
    const [playgroundData, setPlaygroundData] = useState(null);
    const [agentType, setAgentType] = useState("automation");
    const { call, isLoading } = useBluelagoon({ method: "runAutomationPlayground" });
    const { call: callAgent, isLoading: isAgentLoading } = useBluelagoon({ method: "runAgentPlayground" });
    const { execution, executionStatus, runWorkflow, reset } = useWorkflowPolling();

    const handleRun = useCallback(async (params) => {
        reset();
        setAgentType(params.agentType || "automation");
        if (params.agentType === "automation") {
            const result = await call(params);
            if (result?.data) setPlaygroundData(result.data);
        } else {
            const result = await callAgent({ agent: params.agentType, ...params });
            setPlaygroundData({ agentResult: result, agentType: params.agentType });
        }
        setActiveTab(params.agentType === "automation" ? "conversation" : "result");
    }, [call, callAgent, reset]);

    const handleRunWorkflow = useCallback(() => {
        setActiveTab("execution");
        runWorkflow(playgroundData?.workflow?.id);
    }, [playgroundData, runWorkflow]);

    const isAutomation = agentType === "automation";
    const workflowId = playgroundData?.workflow?.id;

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">{t("admin.automationPlayground.title")}</h1>
                    <p className="text-muted-foreground text-sm mt-1">{t("admin.automationPlayground.description")}</p>
                </div>
                {isAutomation && workflowId && (
                    <Button onClick={handleRunWorkflow} disabled={executionStatus === "polling"} variant="default">
                        {executionStatus === "polling"
                            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("admin.automationPlayground.running")}</>
                            : <><Play className="h-4 w-4 mr-2" />{t("admin.automationPlayground.runWorkflow")}</>}
                    </Button>
                )}
            </div>
            <PlaygroundInput onRun={handleRun} isLoading={isLoading || isAgentLoading} />
            {playgroundData && (
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        {isAutomation ? <AutomationTabTriggers /> : (<>
                            <TabsTrigger value="result" className="gap-1.5">
                                <MessageSquare className="h-4 w-4" />{t("admin.automationPlayground.tabs.result")}
                            </TabsTrigger>
                            <TabsTrigger value="conversation" className="gap-1.5">
                                <MessageSquare className="h-4 w-4" />{t("admin.automationPlayground.tabs.conversation")}
                            </TabsTrigger>
                        </>)}
                        <TabsTrigger value="debug" className="gap-1.5">
                            <Bug className="h-4 w-4" />{t("admin.automationPlayground.tabs.debug")}
                        </TabsTrigger>
                    </TabsList>
                    {isAutomation
                        ? <AutomationTabContents data={playgroundData} execution={execution} executionStatus={executionStatus} />
                        : (<>
                            <TabsContent value="result"><AgentResultTab data={playgroundData} /></TabsContent>
                            <TabsContent value="conversation">
                                <ConversationTab data={{ debug: playgroundData?.agentResult?.debug }} />
                            </TabsContent>
                        </>)}
                    <TabsContent value="debug"><DebugTab data={playgroundData} /></TabsContent>
                </Tabs>
            )}
        </div>
    );
}
